import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { Mask } from 'embed/constants'
import { formatCurrency } from 'embed/utils'

import FormText from 'embed/components/FormText'
import { useTheme } from 'embed/hooks'

const styles = require('./styles.scss')

const STEP_AMOUNT = 500
const MIN_AMOUNT = 0
const MAX_AMOUNT = 50000
const DEFAULT_AMOUNT = 5000

const InputStyles = {
  field: styles.amount,
  fieldError: '',
  fieldFocus: '',
  fieldInput: '',
  fieldLabel: '',
}

interface Props {
  name: string
  label?: React.ReactNode
  labelTooltip: React.ReactNode
  value?: number
  minAmount?: number
  maxAmount?: number
  canOverflowMaxAmount?: boolean
  onChange: (value: number) => void
}

const SliderAmountInput: React.FC<Props> = ({
  minAmount,
  maxAmount,
  canOverflowMaxAmount,
  onChange,
  value,
  name,
  label,
  labelTooltip,
}) => {
  const getMinAmount = React.useCallback(() => (minAmount !== undefined ? minAmount : MIN_AMOUNT), [minAmount])

  const getMaxAmount = React.useCallback(() => (maxAmount !== undefined ? maxAmount : MAX_AMOUNT), [maxAmount])

  const constrainAmount = React.useCallback(
    (amount: number) => {
      if (canOverflowMaxAmount) {
        return Math.max(amount, getMinAmount())
      }
      return Math.min(Math.max(amount, getMinAmount()), getMaxAmount())
    },
    [canOverflowMaxAmount, getMinAmount, getMaxAmount],
  )

  const onInputChange = React.useCallback(
    (_, val: string) => onChange(parseInt(val.replace(/\D/g, ''), 10)),
    [onChange],
  )

  const onSliderChange = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => onChange(constrainAmount(parseInt(e.currentTarget.value, 10))),
    [onChange, constrainAmount],
  )

  const getAmount = React.useCallback(() => (value !== undefined ? value : DEFAULT_AMOUNT), [value])

  const onDecrement = React.useCallback(
    () => onChange(constrainAmount(getAmount() - STEP_AMOUNT)),
    [onChange, constrainAmount, getAmount],
  )

  const onIncrement = React.useCallback(
    () => onChange(constrainAmount(getAmount() + STEP_AMOUNT)),
    [onChange, constrainAmount, getAmount],
  )

  const { backdropStyles, colorBorder, colorPrimary, colorTextSecondary } = useTheme()
  return (
    <>
      <div
        className={classnames(
          styles.container,
          css`
            background-color: ${backdropStyles.backgroundColor};
            border-color: ${colorBorder};
          `,
        )}
      >
        <div className={styles.header}>
          <div className={styles.label}>
            {label}
            {labelTooltip}
          </div>
          <FormText
            fieldName={name}
            mask={Mask.Currency}
            onChange={onInputChange}
            styles={InputStyles}
            type="tel"
            value={getAmount().toString()}
          />
        </div>
        <div className={styles.controls}>
          <div
            className={classnames(
              styles.button,
              styles.minus,
              css`
                background-color: ${colorPrimary} !important;
              `,
            )}
            onClick={onDecrement}
          >
            -
          </div>
          <input
            className={css`
                    &::-webkit-slider-thumb {
                      background: ${colorPrimary}};
                    }
                  `}
            max={getMaxAmount()}
            min={getMinAmount()}
            onChange={onSliderChange}
            step={STEP_AMOUNT}
            type="range"
            value={getAmount()}
          />
          <div
            className={classnames(
              styles.button,
              styles.plus,
              css`
                background-color: ${colorPrimary} !important;
              `,
            )}
            onClick={onIncrement}
          >
            +
          </div>
        </div>
        <div
          className={classnames(
            styles.minMaxContainer,
            css`
              color: ${colorTextSecondary};
            `,
          )}
        >
          <div>{`$${formatCurrency(getMinAmount())}`}</div>
          <div>{`$${formatCurrency(getMaxAmount())}${canOverflowMaxAmount ? '+' : ''}`}</div>
        </div>
      </div>
    </>
  )
}

export default SliderAmountInput
